// Client typé de l'API WHCC (contrat partagé avec le backend).

export type Channel = 'stable' | 'canary';
export type DefaultTarget = 'ask' | 'cloud' | 'local';
export type LaunchTarget = 'cloud' | 'local';
export type ProvisioningMode = 'disabled' | 'fake' | 'real';
export type InstanceStatus = 'provisioning' | 'ready' | 'failed' | 'removed';

export interface User {
  id: string;
  username: string;
  displayName: string;
  email: string;
}

export interface Me {
  user: User;
  roles: string[];
  isAdmin: boolean;
  defaultTarget: DefaultTarget;
  provisioningMode: ProvisioningMode;
  baseDomain: string;
}

export interface Instance {
  id: string;
  kind: 'cloud' | 'local';
  status: InstanceStatus;
  channel: Channel;
  url: string | null;
  localPort: number | null;
  version: string | null;
  lastSeenAt: string | null;
  createdAt: string;
}

export type JobType = 'provision' | 'update-channel' | 'remove';
export type JobStatus = 'queued' | 'running' | 'succeeded' | 'failed';
export type StepStatus = 'pending' | 'running' | 'done' | 'failed' | 'skipped';

export interface JobStep {
  name: string;
  status: StepStatus;
  detail?: string;
  at?: string;
}

export interface Job {
  id: string;
  type: JobType;
  status: JobStatus;
  dryRun: boolean;
  username: string;
  steps: JobStep[];
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface AdminUser extends User {
  lastLoginAt: string | null;
  instances: Instance[];
}

export interface PlanStep {
  step: string;
  description: string;
}

export interface AuditEntry {
  id: number;
  at: string;
  actorUsername: string | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  details: Record<string, unknown>;
}

// --- Phase 2 : catalogues, flags, attributions, agent (docs/api.md § Phase 2) ---------------------------------------

export type PluginRisk = 'low' | 'medium' | 'high';
export type PluginSource = 'registry' | 'tarball';
export type VersionStatus = 'draft' | 'published' | 'withdrawn';
export type DriftPolicy = 'enforce' | 'report-only';
export type FlagType = 'boolean' | 'string' | 'number';
export type FlagFamily = 'entitlement' | 'rollout' | 'config';
export type FlagScope = 'instance' | 'user' | 'channel' | 'global';
export type FlagValue = boolean | string | number;

export interface PluginVersion {
  id: string;
  version: string;
  source: PluginSource;
  /** `null` pour une archive hébergée en attente de téléversement. */
  integrity: string | null;
  /** Archive à télécharger : `tarball` seulement ; `null` tant que l'archive hébergée n'est pas téléversée. */
  url?: string | null;
  /** Archive hébergée par WHCC (téléversée) et sa taille en octets. */
  hosted?: boolean;
  sizeBytes?: number | null;
  status: VersionStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CatalogPlugin {
  id: string;
  package: string;
  title: string;
  description: string;
  risk: PluginRisk;
  hasDependencies: boolean;
  dependsOn: string[];
  enabledByDefault: boolean;
  createdAt: string;
  updatedAt: string;
  versions: PluginVersion[];
}

export interface PluginInput {
  package: string;
  title: string;
  description?: string;
  risk?: PluginRisk;
  hasDependencies?: boolean;
  dependsOn?: string[];
  enabledByDefault?: boolean;
}

export interface VersionInput {
  version: string;
  source: PluginSource;
  /** Absente (avec `url`) pour une archive à téléverser dans WHCC. */
  integrity?: string;
  url?: string;
  status?: VersionStatus;
}

export interface SkillPack {
  id: string;
  name: string;
  namespace: string;
  repo: string;
  ref: string;
  /** Sous-dossier du dépôt contenant les skills (optionnel). */
  path?: string | null;
  locked: boolean;
  mandatory: boolean;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface SkillPackInput {
  name: string;
  namespace: string;
  repo: string;
  ref: string;
  path?: string | null;
  locked?: boolean;
  mandatory?: boolean;
  description?: string;
}

export interface FlagRule {
  id: string;
  flagKey: string;
  scope: FlagScope;
  scopeId: string | null;
  value: FlagValue;
  percentage: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface FlagDefinition {
  key: string;
  type: FlagType;
  defaultValue: FlagValue;
  family: FlagFamily;
  description: string;
  createdAt: string;
  updatedAt: string;
  rules: FlagRule[];
}

export interface FlagInput {
  key: string;
  type: FlagType;
  defaultValue: FlagValue;
  family: FlagFamily;
  description?: string;
}

export interface RuleInput {
  scope: FlagScope;
  scopeId?: string | null;
  value: FlagValue;
  percentage?: number | null;
}

export interface InstanceAssignments {
  plugins: { pluginId: string; package: string; pinnedVersion: string | null; enabled: boolean }[];
  skillPacks: { skillPackId: string; name: string; pinnedRef: string | null; enabled: boolean }[];
}

export interface ManifestPreview {
  manifest: {
    schemaVersion: number;
    instanceId: string;
    revision: number;
    generatedAt: string;
    channel: Channel;
    plugins: {
      package: string;
      version: string;
      source: PluginSource;
      integrity?: string;
      url?: string;
      enabled: boolean;
      hasDependencies: boolean;
    }[];
    skills: { name: string; namespace: string; repo: string; ref: string; path?: string; locked: boolean }[];
    mcp: unknown[];
    flags: Record<string, FlagValue>;
    driftPolicy: DriftPolicy;
  };
  warnings: string[];
}

/** Dernier rapport reçu de l'agent : `report` est le JSON du rapport tel que stocké (à lire de façon défensive). */
export interface InstanceReport {
  report: Record<string, unknown>;
  appliedRevision: number | null;
  state: 'ok' | 'degraded' | 'failed';
  receivedAt: string;
}

export interface AgentTokenInfo {
  id: string;
  instanceId: string;
  prefix: string;
  createdAt: string;
  createdBy: string | null;
  revokedAt: string | null;
  lastUsedAt: string | null;
}

/** Réponse de l'émission : `token` n'est renvoyé qu'UNE fois et ne doit jamais être conservé au-delà de l'état d'un composant. */
export interface IssuedAgentToken {
  token: string;
  id: string;
  prefix: string;
  instanceId: string;
  createdAt: string;
  pushedToCluster: boolean;
}

export interface AuditTarget {
  type: string;
  id: string;
}

export type CreateInstanceResult = { instance: Instance; job: Job } | { plan: PlanStep[] };

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export const LOGIN_URL = '/api/auth/login';

const seg = encodeURIComponent;

/** Corps binaire (téléversement d'archive) : envoyé tel quel, avec son type de contenu. */
interface RawBody {
  data: Blob;
  contentType: string;
}

async function request<T>(method: string, path: string, body?: unknown, raw?: RawBody): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin' };
  if (raw) {
    init.headers = { 'Content-Type': raw.contentType };
    init.body = raw.data;
  } else if (method !== 'GET') {
    init.headers = { 'Content-Type': 'application/json' };
    if (body !== undefined) init.body = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch {
    throw new ApiError(0, 'Impossible de joindre le serveur.');
  }
  if (res.status === 401) {
    window.location.assign(LOGIN_URL);
    // La page va être remplacée : on ne résout jamais pour éviter un flash d'erreur.
    return new Promise<T>(() => undefined);
  }
  const text = res.status === 204 ? '' : await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }
  if (!res.ok) {
    const msg =
      data &&
      typeof data === 'object' &&
      'error' in data &&
      typeof (data as { error: unknown }).error === 'string'
        ? (data as { error: string }).error
        : `Erreur ${res.status}`;
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

export function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Erreur inconnue.';
}

export function launchUrl(target?: LaunchTarget, remember = false): string {
  if (!target) return '/api/launch';
  return `/api/launch?target=${target}${remember ? '&remember=1' : ''}`;
}

export const api = {
  me: () => request<Me>('GET', '/api/me'),
  instances: async () => (await request<{ instances: Instance[] }>('GET', '/api/instances')).instances,
  setPreference: (defaultTarget: DefaultTarget) =>
    request<{ defaultTarget: DefaultTarget }>('PUT', '/api/me/preferences', { defaultTarget }),
  setLocalInstance: (port: number) => request<Instance>('PUT', '/api/me/local-instance', { port }),
  deleteLocalInstance: () => request<void>('DELETE', '/api/me/local-instance'),
  logout: () => request<{ logoutUrl: string | null }>('POST', '/api/auth/logout'),

  adminUsers: async () => (await request<{ users: AdminUser[] }>('GET', '/api/admin/users')).users,
  createInstance: (username: string, channel: Channel, dryRun = false) =>
    request<CreateInstanceResult>(
      'POST',
      '/api/admin/instances',
      dryRun ? { username, channel, dryRun } : { username, channel },
    ),
  changeChannel: (id: string, channel: Channel) =>
    request<{ job: Job }>('PATCH', `/api/admin/instances/${encodeURIComponent(id)}`, { channel }),
  retryInstance: (id: string) =>
    request<{ job: Job }>('POST', `/api/admin/instances/${encodeURIComponent(id)}/retry`, {}),
  removeInstance: (id: string) =>
    request<{ job: Job }>('DELETE', `/api/admin/instances/${encodeURIComponent(id)}`),
  jobs: async (limit = 50) => (await request<{ jobs: Job[] }>('GET', `/api/admin/jobs?limit=${limit}`)).jobs,
  job: async (id: string) =>
    (await request<{ job: Job }>('GET', `/api/admin/jobs/${encodeURIComponent(id)}`)).job,
  audit: async (limit = 100, target?: AuditTarget) => {
    const q = new URLSearchParams({ limit: String(limit) });
    if (target) {
      q.set('targetType', target.type);
      q.set('targetId', target.id);
    }
    return (await request<{ entries: AuditEntry[] }>('GET', `/api/admin/audit?${q.toString()}`)).entries;
  },

  // Catalogue de plugins
  plugins: async () => (await request<{ plugins: CatalogPlugin[] }>('GET', '/api/admin/plugins')).plugins,
  createPlugin: async (input: PluginInput) =>
    (await request<{ plugin: CatalogPlugin }>('POST', '/api/admin/plugins', input)).plugin,
  updatePlugin: async (id: string, patch: Partial<Omit<PluginInput, 'package'>>) =>
    (await request<{ plugin: CatalogPlugin }>('PATCH', `/api/admin/plugins/${seg(id)}`, patch)).plugin,
  deletePlugin: (id: string) => request<void>('DELETE', `/api/admin/plugins/${seg(id)}`),
  addPluginVersion: async (id: string, input: VersionInput) =>
    (await request<{ version: PluginVersion }>('POST', `/api/admin/plugins/${seg(id)}/versions`, input))
      .version,
  setPluginVersionStatus: async (id: string, version: string, status: VersionStatus) =>
    (
      await request<{ version: PluginVersion }>(
        'PATCH',
        `/api/admin/plugins/${seg(id)}/versions/${seg(version)}`,
        { status },
      )
    ).version,
  deletePluginVersion: (id: string, version: string) =>
    request<void>('DELETE', `/api/admin/plugins/${seg(id)}/versions/${seg(version)}`),
  /** Téléverse l'archive (.tgz de `npm pack`) d'une version `tarball` en brouillon ; le serveur la contrôle et calcule l'empreinte. */
  uploadPluginArtifact: async (id: string, version: string, file: Blob) =>
    (
      await request<{ version: PluginVersion }>(
        'PUT',
        `/api/admin/plugins/${seg(id)}/versions/${seg(version)}/artifact`,
        undefined,
        { data: file, contentType: 'application/gzip' },
      )
    ).version,
  deletePluginArtifact: async (id: string, version: string) =>
    (
      await request<{ version: PluginVersion }>(
        'DELETE',
        `/api/admin/plugins/${seg(id)}/versions/${seg(version)}/artifact`,
      )
    ).version,

  // Packs de skills
  skillPacks: async () =>
    (await request<{ skillPacks: SkillPack[] }>('GET', '/api/admin/skill-packs')).skillPacks,
  createSkillPack: async (input: SkillPackInput) =>
    (await request<{ skillPack: SkillPack }>('POST', '/api/admin/skill-packs', input)).skillPack,
  updateSkillPack: async (id: string, patch: Partial<Omit<SkillPackInput, 'name'>>) =>
    (await request<{ skillPack: SkillPack }>('PATCH', `/api/admin/skill-packs/${seg(id)}`, patch)).skillPack,
  deleteSkillPack: (id: string) => request<void>('DELETE', `/api/admin/skill-packs/${seg(id)}`),

  // Flags
  flags: async () => (await request<{ flags: FlagDefinition[] }>('GET', '/api/admin/flags')).flags,
  createFlag: async (input: FlagInput) =>
    (await request<{ flag: FlagDefinition }>('POST', '/api/admin/flags', input)).flag,
  updateFlag: async (
    key: string,
    patch: { defaultValue?: FlagValue; family?: FlagFamily; description?: string },
  ) => (await request<{ flag: FlagDefinition }>('PATCH', `/api/admin/flags/${seg(key)}`, patch)).flag,
  deleteFlag: (key: string) => request<void>('DELETE', `/api/admin/flags/${seg(key)}`),
  putFlagRule: async (key: string, rule: RuleInput) =>
    (await request<{ rule: FlagRule }>('PUT', `/api/admin/flags/${seg(key)}/rules`, rule)).rule,
  deleteFlagRule: (key: string, ruleId: string) =>
    request<void>('DELETE', `/api/admin/flags/${seg(key)}/rules/${seg(ruleId)}`),

  // Instance : attributions, dérive, aperçu, rapport, jeton d'agent
  assignments: (instanceId: string) =>
    request<InstanceAssignments>('GET', `/api/admin/instances/${seg(instanceId)}/assignments`),
  setPluginAssignment: (
    instanceId: string,
    pluginId: string,
    body: { enabled: boolean; pinnedVersion: string | null },
  ) =>
    request<InstanceAssignments>(
      'PUT',
      `/api/admin/instances/${seg(instanceId)}/plugins/${seg(pluginId)}`,
      body,
    ),
  removePluginAssignment: (instanceId: string, pluginId: string) =>
    request<void>('DELETE', `/api/admin/instances/${seg(instanceId)}/plugins/${seg(pluginId)}`),
  setSkillPackAssignment: (
    instanceId: string,
    packId: string,
    body: { enabled: boolean; pinnedRef: string | null },
  ) =>
    request<InstanceAssignments>(
      'PUT',
      `/api/admin/instances/${seg(instanceId)}/skill-packs/${seg(packId)}`,
      body,
    ),
  removeSkillPackAssignment: (instanceId: string, packId: string) =>
    request<void>('DELETE', `/api/admin/instances/${seg(instanceId)}/skill-packs/${seg(packId)}`),
  setDriftPolicy: (instanceId: string, driftPolicy: DriftPolicy) =>
    request<{ driftPolicy: DriftPolicy }>('PUT', `/api/admin/instances/${seg(instanceId)}/drift-policy`, {
      driftPolicy,
    }),
  manifestPreview: (instanceId: string) =>
    request<ManifestPreview>('GET', `/api/admin/instances/${seg(instanceId)}/manifest`),
  instanceReport: async (instanceId: string) =>
    (
      await request<{ report: InstanceReport | null }>(
        'GET',
        `/api/admin/instances/${seg(instanceId)}/report`,
      )
    ).report,
  agentTokens: async (instanceId: string) =>
    (
      await request<{ tokens: AgentTokenInfo[] }>(
        'GET',
        `/api/admin/instances/${seg(instanceId)}/agent-tokens`,
      )
    ).tokens,
  issueAgentToken: (instanceId: string, pushToCluster: boolean) =>
    request<IssuedAgentToken>('POST', `/api/admin/instances/${seg(instanceId)}/agent-token`, {
      pushToCluster,
    }),
  revokeAgentTokens: (instanceId: string) =>
    request<{ revoked: number }>('DELETE', `/api/admin/instances/${seg(instanceId)}/agent-token`),
};
